// Public boundary for the layout contract.
//
// pi-server is the validator of record: every LayoutConfigV1 the client ingests
// was validated by the server's strict Ajv schema on write (HTTP 400 otherwise),
// so the client does NOT re-validate field shapes. readLayoutConfig branches on
// schemaVersion only — the one thing a forward-deployed server can emit that an
// older client cannot interpret — and otherwise trusts the payload.
//
// This trust is sound ONLY because layout configs reach the client exclusively
// through server responses; the client never parses a layout file locally. That
// invariant is a documented convention (not mechanically enforced): obtain a
// typed config via readLayoutConfig() on a server response, import contract
// types from this barrel, and never cast raw JSON to LayoutConfigV1/
// WorkspaceStateV1 outside this file.
import type { LayoutConfigV1 } from "./types"

// The barrel is the contract's complete public surface — consumers import every type
// from here, never ./types. So re-export the sub-types too, not just the top-level shapes.
export type {
  LayoutConfigV1,
  WorkspaceStateV1,
  Panel,
  PanelKey,
  PanelTitle,
  AssetViewPanel,
  KpiPanel,
  GraphPanel,
  FactorsPanel,
  AssetViewConfig,
  KpiConfig,
  GraphConfig,
  FactorsConfig,
  GridOrderedFields,
  GridPinnedColumns,
  GridSortModel,
  Core,
  ModuleEntry,
  InstrumentRef,
  NullableInstrumentRef,
  IsoDate,
} from "./types"

export const CURRENT_LAYOUT_SCHEMA_VERSION = 1

const EMPTY_PANELS: LayoutConfigV1["panels"] = []
Object.freeze(EMPTY_PANELS)

export const DEFAULT_LAYOUT_CONFIG: LayoutConfigV1 = Object.freeze({
  schemaVersion: CURRENT_LAYOUT_SCHEMA_VERSION,
  panels: EMPTY_PANELS,
})

export type ReadLayoutConfigResult =
  | { status: "ok", config: LayoutConfigV1 }
  | { status: "version-skew", schemaVersion: number, raw: unknown }
  | { status: "empty" }

/**
 * Resolve a layout config received from the server into a usable result.
 *
 * Not a validator: the server already validated the shape on write. The only
 * runtime branch is schemaVersion — a config newer than this client returns
 * "version-skew", carrying the raw payload (typed `unknown`, since this client
 * can't interpret a newer shape) so the caller can render a placeholder and
 * re-export it verbatim on save rather than dropping it.
 */
export function readLayoutConfig(raw: unknown): ReadLayoutConfigResult {
  if (raw === null || typeof raw !== "object") {
    return { status: "empty" }
  }
  const schemaVersion = (raw as { schemaVersion?: unknown }).schemaVersion
  if (typeof schemaVersion !== "number" || !Number.isInteger(schemaVersion) || schemaVersion < 1) {
    return { status: "empty" }
  }
  if (schemaVersion > CURRENT_LAYOUT_SCHEMA_VERSION) {
    return { status: "version-skew", schemaVersion, raw }
  }
  // The one sanctioned trust cast: schemaVersion checked above + server-validated on
  // write => safe here (rationale/convention: file header). Accepts version
  // <= current; the server upcasts older configs on read, so only current-version
  // payloads arrive in practice. Add a downlevel branch if v2 ever ships without that.
  return { status: "ok", config: raw as LayoutConfigV1 }
}
